import { isEqual } from 'lodash';
import { isNull, isUndefined, uniq } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { Button } from '../../shared/button/Button';
import { Dropdown, DropdownOption } from '../../shared/dropdown/Dropdown';
import { LongText } from '../../shared/longtext/LongText';
import { DraggablePositionModal } from '../../shared/modal/DraggablePositionModal';
import { Position } from '../../shared/modal/PositionModal';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { Text } from '../../shared/text/Text';
import { Toggle } from '../../shared/toggle';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { getAvailableParentCounterpartyTypes, getMultiParentCounterpartyTypes, getSavedParentCounterpartyTypes, getSavedSelectedSubCounterpartyType, getUnavailableParentCounterpartyTypes, revertOpinionSubCounterparty, SubCounterpartyType, updateSubCounterpartiesToDelete, updateMultiParentCounterparties, updateOpinionSubCounterparty, upsertOpinionSubCounterpartyTypesStarted } from './store';
import styles from './SubCounterpartyTypes.module.scss';
import { OpinionScope } from '../opinions/store';
import { scopeFormatter } from '../../shared/table/arkTableFormatters';
import { DropdownSummary } from '../../shared/dropdown/DropdownSummary';
import { getInternetGatewayOpen } from '../../auth/login/store';

const { red, lightestGrey } = styles;

interface SubCounterpartyInputProps {
    subCounterpartyTypes: SubCounterpartyType[];
    selectedSubCounterparty: SubCounterpartyType | null;
    userHasIndustryStandardPermission: boolean;
    initialOffset: Position;
    shouldShowDelete?: boolean;
    opinionScope?: OpinionScope | null;
}

export const SubCounterpartyInput: React.FC<SubCounterpartyInputProps> = ({ subCounterpartyTypes, selectedSubCounterparty, userHasIndustryStandardPermission, initialOffset, shouldShowDelete = false, opinionScope = null }) => {
    const dispatch = useAppDispatch();

    const savedSelectedSubCounterparty = useAppSelector(getSavedSelectedSubCounterpartyType);
    const multiParentCounterpartyTypes = useAppSelector(getMultiParentCounterpartyTypes);
    const unavailableParentCounterpartyTypes = useAppSelector(getUnavailableParentCounterpartyTypes);
    const availableParentCounterpartyTypes = useAppSelector(getAvailableParentCounterpartyTypes);
    const savedParentCounterpartyTypes = useAppSelector(getSavedParentCounterpartyTypes);
    const internetGatewayOpen = useAppSelector(getInternetGatewayOpen);
    const subCounterpartyId = useMemo(() => selectedSubCounterparty?.subCounterpartyTypeId || null, [selectedSubCounterparty]);
    const updateSubCounterpartyValue = useCallback((key: keyof SubCounterpartyType, value: string | number | OpinionScope[]) => dispatch(updateOpinionSubCounterparty(key, value)), [dispatch]);

    const updateSubCounterpartyDescription = useCallback((value: string) => {
        const updatedValue = value === '' ? null : value;
        dispatch(updateOpinionSubCounterparty('subCounterpartyDescription', updatedValue));
    }, [dispatch]);

    const openDeleteSubCounterpartyType = useCallback(() => !isNull(savedSelectedSubCounterparty) && dispatch(updateSubCounterpartiesToDelete([savedSelectedSubCounterparty])), [dispatch, savedSelectedSubCounterparty]);

    const saveOpinionSubCounterpartyType = useCallback(() => dispatch(upsertOpinionSubCounterpartyTypesStarted()), [dispatch]);

    const revertChanges = useCallback(() => dispatch(revertOpinionSubCounterparty()), [dispatch]);

    const name = useMemo(() => selectedSubCounterparty ? selectedSubCounterparty.subCounterpartyName : '', [selectedSubCounterparty]);
    const description = useMemo(() => selectedSubCounterparty && selectedSubCounterparty.subCounterpartyDescription ? selectedSubCounterparty.subCounterpartyDescription : '', [selectedSubCounterparty]);
    const scopes = useMemo(() => selectedSubCounterparty && selectedSubCounterparty.scopes || [], [selectedSubCounterparty]);

    const scopeOptions = useMemo(() => Object.values(OpinionScope).map(scope => ({ value: scope, label: scopeFormatter(scope) })), []);
    const scopeValue = useMemo(() => scopeOptions.filter(({ value }) => scopes.includes(value)), [scopeOptions, scopes]);

    const subCounterpartyNameExists = useMemo(() => {
        let existingSubCounterpartyTypes = subCounterpartyTypes.filter(subCounterpartyType => subCounterpartyType.scopes.some(scope => scopes.includes(scope)));
        if (selectedSubCounterparty?.subCounterpartyTypeId) {
            existingSubCounterpartyTypes = existingSubCounterpartyTypes.filter(({ subCounterpartyTypeId, parentCounterpartyType }) => (subCounterpartyTypeId !== selectedSubCounterparty.subCounterpartyTypeId) && parentCounterpartyType === selectedSubCounterparty.parentCounterpartyType);
        } else {
            existingSubCounterpartyTypes = existingSubCounterpartyTypes.filter(({ parentCounterpartyType }) => multiParentCounterpartyTypes.includes(parentCounterpartyType));
        }
        return existingSubCounterpartyTypes.some(({ subCounterpartyName }) => subCounterpartyName.toLowerCase() === selectedSubCounterparty?.subCounterpartyName.toLowerCase());
    }, [subCounterpartyTypes, selectedSubCounterparty, multiParentCounterpartyTypes, scopes]);

    const isSystemSubCounterparty = useMemo(() => !!selectedSubCounterparty?.isSystem || false, [selectedSubCounterparty]);

    const selectedSubCounterpartyUpdated = useMemo(() => !isEqual(selectedSubCounterparty, savedSelectedSubCounterparty) || !isEqual(savedParentCounterpartyTypes, multiParentCounterpartyTypes), [selectedSubCounterparty, savedSelectedSubCounterparty, savedParentCounterpartyTypes, multiParentCounterpartyTypes]);

    const showDelete = useMemo(() => !isNull(subCounterpartyId) && !selectedSubCounterpartyUpdated && shouldShowDelete && !internetGatewayOpen, [subCounterpartyId, selectedSubCounterpartyUpdated, shouldShowDelete, internetGatewayOpen]);

    const saveDisabledTooltip = useMemo(() => {
        let disabledTooltip = [];
        if (name === '' && selectedSubCounterpartyUpdated) {
            disabledTooltip.push('A sub counterparty can not be saved without a name.');
        }
        if (subCounterpartyNameExists) {
            disabledTooltip.push('The name you have added cannot be added for the parent counterparty as it already exists.');
        }
        if (isNull(subCounterpartyId) && multiParentCounterpartyTypes.length === 0) {
            disabledTooltip.push('You must choose at least one parent counterparty type.');
        }
        if (scopes.length === 0) {
            disabledTooltip.push('You must choose at least one scope');
        }
        return disabledTooltip;
    }, [name, selectedSubCounterpartyUpdated, subCounterpartyNameExists, subCounterpartyId, multiParentCounterpartyTypes, scopes]);

    const updateParentCounterparties = useCallback((option: DropdownOption | Options<DropdownOption> | null) => {
        let dropdownValue: string[] = savedParentCounterpartyTypes;
        if (!isNull(option)) {
            dropdownValue = uniq([...dropdownValue, ...(option as Options<DropdownOption>).map(({ value }) => value)]);
        }
        dispatch(updateMultiParentCounterparties(dropdownValue));
    }, [dispatch, savedParentCounterpartyTypes]);

    const updateScopes = useCallback((option: DropdownOption | Options<DropdownOption> | null) => {
        let dropdownValue: OpinionScope[] = !isNull(opinionScope) ? [opinionScope] : [];
        if (!isNull(option)) {
            dropdownValue = uniq([...dropdownValue, ...((option as Options<DropdownOption>).map(({ value }) => value) as OpinionScope[])]);
        }
        updateSubCounterpartyValue('scopes', dropdownValue);
    }, [updateSubCounterpartyValue, opinionScope]);

    const saveDisabled = useMemo(() => (name.length === 0 && selectedSubCounterpartyUpdated) || subCounterpartyNameExists || !selectedSubCounterpartyUpdated || (isNull(subCounterpartyId) && multiParentCounterpartyTypes.length === 0) || scopes.length === 0 || internetGatewayOpen, [name, selectedSubCounterpartyUpdated, subCounterpartyNameExists, subCounterpartyId, multiParentCounterpartyTypes, scopes.length, internetGatewayOpen]);
    const getHighlightColour = useCallback((parentCounterpartyType: string) => {
        const highlightColour = isUndefined(selectedSubCounterparty?.subCounterpartyTypeId) ? red : lightestGrey;
        return unavailableParentCounterpartyTypes.includes(parentCounterpartyType) ? highlightColour : undefined;
    }, [unavailableParentCounterpartyTypes, selectedSubCounterparty]);

    const getIsDisabled = useCallback((parentCounterpartyType: string) => !isUndefined(selectedSubCounterparty?.subCounterpartyTypeId) && unavailableParentCounterpartyTypes.includes(parentCounterpartyType), [unavailableParentCounterpartyTypes, selectedSubCounterparty]);

    const parentOptions: DropdownOption[] = useMemo(() => availableParentCounterpartyTypes.map(value => ({ value, label: value, highlightColour: getHighlightColour(value) })), [availableParentCounterpartyTypes, getHighlightColour]);
    const parentCounterpartyValue = useMemo(() => multiParentCounterpartyTypes.map(value => ({ value, label: value, highlightColour: getHighlightColour(value), disabled: getIsDisabled(value) })), [multiParentCounterpartyTypes, getHighlightColour, getIsDisabled]);

    if (isNull(selectedSubCounterparty)) {
        return null;
    }

    return (
        <>
            <DraggablePositionModal
                closeModal={revertChanges}
                initialPosition={initialOffset}
                isOpen={!isNull(selectedSubCounterparty)}
                testId='sub-counterparty-type'
                width='500px'
                height='500px'
            >
                <div className={styles.selectedSubCounterpartyWrapper}>
                    <div className={styles.selectedSubCounterparty}>
                        <Scrollable>
                            <div className={styles.selectedSubCounterpartyInput}>
                                <Text
                                    testId='sub-counterparty-name'
                                    value={name}
                                    onChange={e => updateSubCounterpartyValue('subCounterpartyName', e.target.value)}
                                    maxLength={254}
                                    label='Name'
                                />
                            </div>
                            <div className={styles.selectedSubCounterpartyInput}>
                                <LongText
                                    testId='sub-counterparty-description'
                                    value={description}
                                    onChange={value => updateSubCounterpartyDescription(value)}
                                    maxLength={4000}
                                    width='100%'
                                    label='Description'
                                />
                            </div>
                            <div className={styles.selectedSubCounterpartyInput}>
                                <Dropdown
                                    onChange={updateParentCounterparties}
                                    value={parentCounterpartyValue}
                                    options={parentOptions}
                                    marginBottom='10px'
                                    label='Parent Counterparty'
                                    menuPortalTarget={document.body}
                                    isMulti
                                />
                            </div>
                            <div className={styles.selectedSubCounterpartyInput}>
                                <DropdownSummary
                                    onChange={updateScopes}
                                    value={scopeValue}
                                    options={scopeOptions}
                                    marginBottom='10px'
                                    label='Scope'
                                    menuPortalTarget={document.body}
                                    isMulti
                                />
                            </div>
                            {userHasIndustryStandardPermission &&
                                <div className={styles.selectedSubCounterpartyToggle}>
                                    <div className={styles.selectedSubCounterpartyToggleLabel}>System Sub Counterparty?</div>
                                    <Toggle checked={isSystemSubCounterparty} onChange={checked => updateSubCounterpartyValue('isSystem', checked ? 1 : 0)} disabled={!!subCounterpartyId} />
                                </div>
                            }
                        </Scrollable>
                    </div>
                    <div className={styles.buttonWrapper}>
                        <Button label='Cancel' onClick={revertChanges} />
                        {showDelete && <Button label='Delete' onClick={openDeleteSubCounterpartyType} />}
                        <Button label='Save' onClick={saveOpinionSubCounterpartyType} disabled={saveDisabled} disabledTooltip={saveDisabledTooltip} />
                    </div>
                </div>
            </DraggablePositionModal>
            <DeleteConfirmationModal />
        </>
    );
};
